import { useState } from 'react';

import { usePermissions } from '../auth/usePermissions.js';
import { useTranslation } from '../i18n/useTranslation.js';

import { AuditLogCard } from './AuditLogCard.jsx';
import { CompanySettingsCard } from './CompanySettingsCard.jsx';
import { CurrencySettingsCard } from './CurrencySettingsCard.jsx';
import { EbicsSettingsCard } from './EbicsSettingsCard.jsx';
import { GeneralSettings } from './GeneralSettings.jsx';
import { PasswordChangeCard } from './PasswordChangeCard.jsx';
import { ProfileForm } from './ProfilePage.jsx';
import { UserManagementCard } from './UserManagementCard.jsx';
import { ZeiterfassungSettingsCard } from './ZeiterfassungSettingsCard.jsx';

function GeneralIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <line x1="4" y1="8" x2="20" y2="8" />
      <circle cx="9" cy="8" r="2.4" fill="var(--color-surface)" />
      <line x1="4" y1="16" x2="20" y2="16" />
      <circle cx="15" cy="16" r="2.4" fill="var(--color-surface)" />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="3.2" />
      <path d="M5 20c0-3.9 3.1-7 7-7s7 3.1 7 7" />
    </svg>
  );
}

function CompanyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 20V6l8-2v16" />
      <path d="M12 20V9l8 2v9" />
      <line x1="3" y1="20" x2="21" y2="20" />
      <line x1="7" y1="9" x2="9" y2="9" />
      <line x1="7" y1="13" x2="9" y2="13" />
    </svg>
  );
}

function BankIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9l9-5 9 5" />
      <line x1="4" y1="9" x2="20" y2="9" />
      <line x1="6" y1="9" x2="6" y2="18" />
      <line x1="10" y1="9" x2="10" y2="18" />
      <line x1="14" y1="9" x2="14" y2="18" />
      <line x1="18" y1="9" x2="18" y2="18" />
      <line x1="3" y1="20" x2="21" y2="20" />
    </svg>
  );
}

function RatesIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 9h13l-3-3" />
      <path d="M20 15H7l3 3" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <path d="M16 3.5a3 3 0 0 1 0 5.8" />
      <path d="M21 20c0-2.8-1.7-5.2-4-6" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5l3.2 2" />
    </svg>
  );
}

function AuditIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="9" y1="12" x2="15" y2="12" />
    </svg>
  );
}

export function SettingsPage() {
  const { t } = useTranslation();
  const { canWrite } = usePermissions();

  const sections = [
    { key: 'general', label: t('settings.nav.general'), desc: t('settings.nav.general.desc'), icon: <GeneralIcon /> },
    { key: 'profile', label: t('profile.menu'), desc: t('profile.subtitle'), icon: <ProfileIcon /> },
    { key: 'company', label: t('settings.nav.company'), desc: t('settings.company.intro'), icon: <CompanyIcon /> },
    ...(canWrite('finance')
      ? [
          { key: 'ebics', label: t('settings.nav.ebics'), desc: t('ebics.intro'), icon: <BankIcon /> },
          { key: 'currencies', label: t('settings.nav.currencies'), desc: t('settings.nav.currencies.desc'), icon: <RatesIcon /> },
        ]
      : []),
    ...(canWrite('admin')
      ? [
          /* Ob im Betrieb gestempelt oder getippt wird, entscheidet die
             Geschaeftsleitung - nicht der Anbieter und nicht die
             Personalabteilung. Deshalb steht es hier und nicht unter 'hr'. */
          { key: 'zeit', label: t('settings.nav.zeit'), desc: t('settings.zeit.intro'), icon: <ClockIcon /> },
          { key: 'users', label: t('settings.nav.users'), desc: t('settings.users.intro'), icon: <UsersIcon /> },
          { key: 'audit', label: t('settings.nav.audit'), desc: t('audit.intro'), icon: <AuditIcon /> },
        ]
      : []),
  ];

  const [active, setActive] = useState('general');
  const current = sections.find((s) => s.key === active) || sections[0];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('settings.title')}</h1>
          <p className="muted">{t('settings.subtitle')}</p>
        </div>
      </header>

      <div className="settings-layout">
        <nav className="settings-nav" aria-label={t('settings.title')}>
          {sections.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`settings-nav-item${s.key === current.key ? ' active' : ''}`}
              aria-current={s.key === current.key ? 'page' : undefined}
              onClick={() => setActive(s.key)}
            >
              {s.icon}
              <span>{s.label}</span>
            </button>
          ))}
        </nav>

        <div className="settings-panel">
          <header className="settings-section-head">
            <h2>{current.label}</h2>
            <p>{current.desc}</p>
          </header>

          {current.key === 'general' && <GeneralSettings />}
          {current.key === 'profile' && (
            <>
              <ProfileForm />
              <PasswordChangeCard />
            </>
          )}
          {current.key === 'company' && <CompanySettingsCard />}
          {current.key === 'ebics' && canWrite('finance') && <EbicsSettingsCard />}
          {current.key === 'currencies' && canWrite('finance') && <CurrencySettingsCard />}
          {current.key === 'zeit' && canWrite('admin') && <ZeiterfassungSettingsCard />}
          {current.key === 'users' && canWrite('admin') && <UserManagementCard />}
          {current.key === 'audit' && canWrite('admin') && <AuditLogCard />}
        </div>
      </div>
    </section>
  );
}
